import { useCallback, useContext, useState } from 'react';

import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createContainer } from 'unstated-next';

import { CLASS_SUB_CLASSES, CLASS_UPDATE } from '../../../config/apiPaths';
import { ClassContext } from '../../../context/ClassContext';
import { type ClassRecordFromApi, type SubClassesResponse, type SubClassRecord } from '../../../context/types';
import { Status } from '../../../types/apiTypes';
import { getUserUuidHeaders } from '../../../utils/httpUtils';
import { getUrls, processApiPath } from '../../../utils/url';

interface SubClassContainerState {
    mainClass?: ClassRecordFromApi; // result of the CLASS_GET api call
    subClasses?: ClassRecordFromApi[]; // result of the CLASS_SUB_CLASSES api call
}

const useSubClasses = (state: SubClassContainerState = {}) => {
    const { subClasses: initialSubClasses, mainClass: initialMainClass } = state;
    const { t } = useTranslation('class');
    const userUuid = window.localStorage.getItem('user_uuid') as string;
    const { api } = getUrls();
    const [status, setStatus] = useState<Status>(Status.NotStarted);
    const {
        classData: { class: currentClass }
    } = useContext(ClassContext);

    const [subClasses, setSubClasses] = useState<SubClassRecord[]>(initialSubClasses || []);
    const [mainClass, setMainClass] = useState<SubClassRecord | null>(initialMainClass || null);

    const { enqueueSnackbar } = useSnackbar();

    const fetchSubClasses = useCallback(
        async (force: boolean = false) => {
            if (!currentClass?.sid) return;
            if (!force && status !== Status.NotStarted) return;

            let response: SubClassesResponse | undefined;
            try {
                setStatus(Status.InProgress);

                const url = processApiPath(CLASS_SUB_CLASSES, { classId: String(currentClass?.sid) });
                response = (
                    await axios.get<SubClassesResponse>(url, {
                        headers: getUserUuidHeaders(userUuid)
                    })
                ).data;

                setSubClasses(response.subClasses);
                setMainClass(response.mainClass);
            } catch (err) {
                enqueueSnackbar(t('error-message'), { variant: 'error' });
            }

            setStatus(Status.Complete);
            return response;
        },
        [currentClass?.sid, status, userUuid, enqueueSnackbar, t]
    );

    const lockClassToggle = async (classRecord: Partial<ClassRecordFromApi>) => {
        if (!classRecord.data) {
            classRecord.data = { locked: false } as any;
        }

        try {
            await axios.put(`${api}${CLASS_UPDATE}`, {
                user_uuid: userUuid,
                class_uuid: classRecord.class_uuid,
                class_data: {
                    locked: !classRecord.data?.locked
                }
            });

            const mapData = (subClass: SubClassRecord) =>
                subClass.class_uuid === classRecord.class_uuid
                    ? ({ ...subClass, data: { ...subClass.data, locked: !subClass.data?.locked } } as SubClassRecord)
                    : subClass;

            setMainClass((prevClass) => mapData(prevClass!));
            setSubClasses((prevSubClasses) => prevSubClasses.map(mapData));
        } catch (err) {
            enqueueSnackbar(t('error-message'), { variant: 'error' });
        }
    };

    return {
        status,
        mainClass,
        subClasses,
        setSubClasses,
        fetchSubClasses,
        lockClassToggle
    };
};

export const SubClassContainer = createContainer(useSubClasses);
